// Local Markdown drafts (ADR 0011 decision 7: editing needs a connection; an unsaved draft is kept on the device
// and saved on reconnect). Separate IndexedDB database per origin+brand+user+workspace partition, mirroring the
// naming of src/db/localDb.ts, so it never touches that schema and never leaks across accounts. Not localStorage.
import { openDB, deleteDB, type IDBPDatabase } from 'idb';

export interface MdDraft { fileId: string; text: string; baseVersion: number; updatedAt: number }

export interface DraftStore {
  get(fileId: string): Promise<MdDraft | undefined>;
  put(d: MdDraft): Promise<void>;
  delete(fileId: string): Promise<void>;
  list(): Promise<MdDraft[]>;
}

export interface DraftPartition { brandId: string; userId: string; workspaceId: string }

export function draftDbName(p: DraftPartition, origin = location.origin): string {
  const safe = (s: string) => encodeURIComponent(s);
  return `taskpanes-md|${safe(origin)}|b:${safe(p.brandId)}|u:${safe(p.userId)}|w:${safe(p.workspaceId)}`;
}

export function idbDraftStore(p: DraftPartition, origin?: string): DraftStore {
  let dbp: Promise<IDBPDatabase> | null = null;
  const db = () => (dbp ??= openDB(draftDbName(p, origin), 1, { upgrade(d) { d.createObjectStore('drafts', { keyPath: 'fileId' }); } }));
  return {
    get: async id => (await (await db()).get('drafts', id)) as MdDraft | undefined,
    put: async d => { await (await db()).put('drafts', d); },
    delete: async id => { await (await db()).delete('drafts', id); },
    list: async () => (await (await db()).getAll('drafts')) as MdDraft[],
  };
}

/** Removes the partition's drafts (sign-out with explicit discard, account removal). */
export const deleteDraftPartition = (p: DraftPartition, origin?: string): Promise<void> => deleteDB(draftDbName(p, origin));

/** In-memory store (demo mode, tests). */
export function memoryDraftStore(): DraftStore {
  const m = new Map<string, MdDraft>();
  return {
    get: async id => m.get(id),
    put: async d => { m.set(d.fileId, { ...d }); },
    delete: async id => { m.delete(id); },
    list: async () => [...m.values()],
  };
}
